import { Box, Card, Text, VStack, Flex, Link } from '@chakra-ui/react';
import { TermsList } from '@/components/common/TermSection/TermsList';
import { TermsText } from '@/components/common/TermSection/TermsText';
import { useGetCommissionRate } from '@/services/payment';
import Skeleton from '@/components/common/Skeleton';

const TOC = () => {
  const { data: commissionData, isLoading, error, isFetching } = useGetCommissionRate();
  const renderCommission = () => {
    if (isLoading || isFetching) {
      return <Skeleton h="20px" w="120px" borderRadius="md" display="inline-block" />;
    }

    if (error || !commissionData) {
      return (
        <Text as="span" color="red.500" fontWeight="bold">
          N/A
        </Text>
      );
    }

    const lower = commissionData?.lower_bound ?? 'N/A';
    const upper = commissionData?.upper_bound ?? 'N/A';

    return (
      <Text as="span" color="brand.primary.DEFAULT" fontWeight="bold">
        {lower} - {upper}
      </Text>
    );
  };
  return (
    <Box p={4} fontFamily={'primary'}>
      <VStack spacing={4} align="stretch">
        <Card
          shadow="none"
          rounded="lg"
          bg="background.white"
          borderWidth="1px"
          borderColor="border.default60"
        >
          <Flex p={4} bg="background.tertiary" align="center">
            <Text fontSize="md" fontWeight="semibold">
              About Us
            </Text>
          </Flex>

          <VStack p={4} align="start" spacing={3}>
            {/* Tagline */}
            <Text fontSize="sm" color="text.secondary" fontStyle="italic">
              Where Tradition Meets Contemporary Elegance!
            </Text>

            {/* Main Description – Extended */}
            <Text fontSize="sm" color="text.primary" lineHeight="1.7">
              At{' '}
              <Link href="https://kurtasari.com" color={'brand.primary.DEFAULT'}>
                Kurta Sari
              </Link>
              , we honor the timeless beauty of our culture through our curated collection of Kurtas
              and Saris. Each piece is crafted with love, inspired by generations of artistry and
              craftsmanship.
              <br />
              <br />
              Our journey began with a simple vision: to preserve the rich heritage of South Asian
              fashion while making it accessible to the modern world. From intricate
              hand-embroidered kurtas to flowing, elegant saris, every garment tells a story — of
              tradition, passion, and identity.
              <br />
              <br />
              We partner with skilled artisans across Nepal, India, and beyond, ensuring fair wages
              and sustainable practices. Every stitch reflects quality, every design celebrates
              diversity, and every purchase supports a legacy.
              <br />
              <br />
              Discover clothing that tells <strong>your</strong> story — one thread at a time.
            </Text>
          </VStack>
        </Card>

        <Card
          shadow="none"
          rounded="lg"
          bg="background.white"
          borderWidth="1px"
          borderColor="border.default60"
        >
          <Flex p={4} bg="background.tertiary" align="center">
            <Text fontSize="md" fontWeight="semibold">
              Contact
            </Text>
          </Flex>

          <VStack p={4} align="start" spacing={2}>
            <Text fontSize="sm" color="text.primary">
              <strong>Vendor Support:</strong>{' '}
              <Link
                href="https://wa.me/17202097941"
                isExternal
                color="brand.primary.DEFAULT"
                fontWeight="medium"
                _hover={{ textDecoration: 'underline' }}
              >
                +1 (720) 209-7941 (WhatsApp)
              </Link>
            </Text>

            <Text fontSize="sm" color="text.secondary">
              Kurta Saree LLC, Aurora, Colorado, USA
            </Text>
          </VStack>
        </Card>

        <Card
          shadow="none"
          rounded="lg"
          bg="background.white"
          borderWidth="1px"
          borderColor="border.default60"
        >
          <Flex p={4} bg="background.tertiary" align="center">
            <Text fontSize="md" fontWeight="semibold">
              Kurta Sari Policy
            </Text>
          </Flex>

          <VStack p={5} align="start" spacing={4}>
            <TermsText fontSize="sm" color="text.primary">
              At{' '}
              <Link href="https://kurtasari.com" color={'brand.primary.DEFAULT'}>
                kurtasari.com
              </Link>
              , all vendors are required to follow our product posting and business conduct
              guidelines to ensure a trustworthy shopping experience for customers.
            </TermsText>

            <Box w="full">
              <Text fontWeight="medium" fontSize="sm" mb={2} color="text.primary">
                Product Authenticity
              </Text>
              <TermsList
                items={[
                  <>Vendors must upload and sell only authentic and original products.</>,
                  <>Counterfeit or duplicate products are strictly prohibited.</>,
                  <>
                    Product descriptions and images must accurately represent the actual product.
                  </>,
                ]}
                ordered={false}
                noMargin={true}
              />
            </Box>

            <Box w="full">
              <Text fontWeight="medium" fontSize="sm" mb={2} color="text.primary">
                Product Availability
              </Text>
              <TermsList
                items={[
                  <>Only list items that are currently available in stock.</>,
                  <>Remove or update products that are out of stock immediately.</>,
                  <>
                    Do not post pre-order or unavailable items without clear mention and delivery
                    timelines.
                  </>,
                ]}
                ordered={false}
                noMargin={true}
              />
            </Box>

            <Box w="full">
              <Text fontWeight="medium" fontSize="sm" mb={2} color="text.primary">
                Fair Pricing and Transparency
              </Text>
              <TermsList
                items={[
                  <>All listed prices must be genuine and clearly visible.</>,
                  <>Hidden charges or misleading discounts are not allowed.</>,
                  <>Vendors must follow fair pricing practices at all times.</>,
                ]}
                ordered={false}
                noMargin={true}
              />
            </Box>

            <Box w="full">
              <Text fontWeight="medium" fontSize="sm" mb={2} color="text.primary">
                Image and Content Guidelines
              </Text>
              <TermsList
                items={[
                  <>
                    Product photos must be clear, high-quality, and free of watermarks or unrelated
                    branding.
                  </>,
                  <>Avoid using misleading titles, tags, or descriptions.</>,
                  <>
                    All product information must comply with copyright and intellectual property
                    laws.
                  </>,
                ]}
                ordered={false}
                noMargin={true}
              />
            </Box>

            <Box w="full">
              <Text fontWeight="medium" fontSize="sm" mb={2} color="text.primary">
                Vendor Responsibility
              </Text>
              <TermsText fontSize="sm" color="text.primary">
                Vendors are responsible for the accuracy of their listings, prompt fulfillment of
                orders, maintaining product quality, and providing responsive customer service.
                Failure to comply with these policies may result in suspension or permanent removal
                from the platform.
              </TermsText>
            </Box>

            <Box w="full">
              <Text fontWeight="medium" fontSize="sm" mb={2} color="text.primary">
                Agreement
              </Text>
              <TermsText fontSize="sm" color="text.primary">
                By listing products on{' '}
                <Link href="https://kurtasari.com" color={'brand.primary.DEFAULT'}>
                  kurtasari.com
                </Link>
                , vendors agree to abide by these policies and maintain business integrity at all
                times.
              </TermsText>
            </Box>
          </VStack>
        </Card>

        <Card
          shadow="none"
          rounded="lg"
          bg="background.white"
          borderWidth="1px"
          borderColor="border.default60"
        >
          <Flex p={4} bg="background.tertiary" align="center">
            <Text fontSize="md" fontWeight="semibold">
              Commission Rate
            </Text>
          </Flex>

          <Box p={4}>
            <Text fontSize="sm" color="text.primary">
              <strong>Variable Commission Rate:</strong>{' '}
              <Text as="span" color="brand.primary.DEFAULT" fontWeight="bold">
                <Box as="span" display="inline-block" textAlign="center">
                  {renderCommission()}
                </Box>
              </Text>{' '}
              determined by product price — where commission rates decrease as product prices
              increase.
            </Text>
          </Box>
        </Card>
      </VStack>
    </Box>
  );
};

export default TOC;
